import React, { useEffect, useRef, useState } from 'react'
import { Box, Button, IconButton, Tooltip } from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import { FS } from '../../theme/tokens'

/** The confirmation text. Exported so a handler-driven caller can report the
 *  same wording through its own channel instead of a second copy of the
 *  string (see `PODetail.jsx`). */
export const COPY_OK = 'Copied'

/** The failure text. Reached both when the Clipboard API is missing entirely
 *  (insecure origin, older browser) and when the browser rejects the write
 *  (permission denied) — the two are the same thing to the user, so they get
 *  the same sentence. */
export const COPY_FAIL = 'Copy failed — clipboard not available.'

/** How long the confirmation stays before the button returns to its idle
 *  shape. 2000 ms is the value of the block's model,
 *  `pages/invoicing/InvoiceDetail.jsx` › `KopierKnopf`. `theme/tokens.js`
 *  carries no duration token, so it stays one named literal here instead of a
 *  bare number at every call site. */
const RUECKMELDUNG_MS = 2000

/**
 * copyToClipboard — the ONLY place in the app that reads the Clipboard API.
 *
 * @description
 *   Resolves `true` on a successful write, `false` on every failure — missing
 *   API, missing `writeText`, or a rejected write. It never throws and never
 *   rejects, so a caller cannot end up with an unhandled rejection from a
 *   blocked clipboard. The function check happens BEFORE the call: on an
 *   insecure origin the API object is `undefined`, and calling into it would
 *   throw a `TypeError` that reads like a bug rather than a blocked browser
 *   capability.
 *
 * @props
 *   value — the text to write
 */
export async function copyToClipboard(value) {
  const ablage = typeof navigator === 'undefined' ? null : navigator.clipboard
  if (!ablage || typeof ablage.writeText !== 'function') return false
  try {
    await ablage.writeText(value)
    return true
  } catch {
    return false
  }
}

/**
 * CopyButton — the ONE copy-to-clipboard affordance in the app.
 *
 * @description
 *   Before this file existed, eight call sites wrote to the Clipboard API
 *   themselves — five of them with no confirmation at all, three of them
 *   swallowing a blocked clipboard in a silent `catch {}`. The user pressed
 *   "Copy", nothing happened, and nothing said so. This block is the single
 *   place that touches the Clipboard API, and it always reports the outcome.
 *
 *   The confirmation is carried on two channels at once:
 *   1. the symbol swaps to a check mark and the tooltip reads "Copied" —
 *      that is what a sighted user sees, and on a touch device it is the
 *      ONLY visual feedback there is (a tooltip never opens without hover);
 *   2. a `role="status"` live region, present from the first render, whose
 *      text changes — that is what a screen reader announces.
 *
 *   The live region is rendered from the first paint on purpose. A region
 *   mounted together with its text is announced unreliably or not at all;
 *   a region that is already in the tree and only receives new text is the
 *   pattern that works.
 *
 *   With `statusVisible` that region is a VISIBLE line only once it carries
 *   text — while it is empty it stays visually hidden. It is the same node
 *   throughout; only the style swaps. An always-visible empty block would push
 *   the call site's `Stack` down by one spacing step the moment the block is
 *   added, and `mt: 0` does not repair that: the gap is written by the Stack's
 *   own child selector (`> * + *`), which outranks a plain `margin-top` on the
 *   child. The region must nevertheless stay in the tree from the first paint,
 *   or it is not announced — hence the style swap rather than a conditional
 *   render.
 *
 *   `reserveStatusLine` is the exception, and the reservation is EXPLICIT: the
 *   region keeps `display: block` from the first paint AND carries a
 *   non-breaking space while it has no message, so in the idle state it already
 *   holds the height of one line at `FS.sm`. A call site whose cell reserves the
 *   line (a grid cell) therefore does not grow when the confirmation appears and
 *   shrink again two seconds later. The mechanism is the non-breaking space, not
 *   the `display`: an empty block box has no line box and is 0 px tall, which is
 *   why the first version of this prop reserved nothing at all. House pattern for
 *   a reserved line: `components/common/StatTile.jsx:396`
 *   (`{hint != null && hint !== '' ? hint : ' '}`).
 *
 *   The model `KopierKnopf` did NOT reserve its line — it rendered the same
 *   EMPTY `display: block` span
 *   (`20142dbac5:pages/invoicing/InvoiceDetail.jsx:766-769`) and jumped on every
 *   confirmation, exactly like the un-reserved default here. The prop is not
 *   "the model's behaviour"; it repairs a defect the model carried.
 *
 *   That is the opposite trade-off to `statusVisible` alone — the line costs
 *   height in the idle state — and it is only right at a site that wants that
 *   height anyway; a `Stack` gains a spacing step there instead. Hence the prop,
 *   not a second default. Without `statusVisible` it is a no-op, and it says so
 *   in development (see the body).
 *
 *   While the confirmation holds, the button carries `data-copied="true"`.
 *   That is what lets a call site style the confirmation state itself: the RMA
 *   header lifts its dimmed `opacity` there, because on a touch device the
 *   check mark is the only visible feedback and a 55 %-dimmed one is barely
 *   one.
 *
 * @useWhen
 *   - Any "copy this value" affordance: an id, a link, a payload, a body of text
 *   - An icon-only copy next to a row (address, contact, share link)
 *   - A labelled "Copy to clipboard" button under a code or payload block
 *   - A handler-driven copy that is not a button of its own — import
 *     `copyToClipboard` and surface `COPY_OK` / `COPY_FAIL` in the caller's
 *     own feedback channel (see `PODetail.jsx` and its toast)
 *
 * @dontUseFor
 *   - Cutting or pasting into an input — that is `TextField` behaviour, not this
 *   - A "duplicate record" action — that is a write, not a clipboard transfer
 *   - A copy that is only a step inside a longer state machine where the button
 *     is replaced by the next step: pass `onCopied` so the caller advances, and
 *     do not also render a second confirmation next to it
 *
 * @related
 *   - `rma/components/InfoPrimitives.jsx` › `CopyIcon` — the thin wrapper kept
 *     for the RMA header; same block, `title` kept as the prop name
 *   - `RefreshButton` — the same "one affordance, one block" shape
 *
 * @reuseHints
 *   - Any new detail page with an id, a number or a deep link worth passing on
 *   - Vendor or customer contact rows that currently carry a bare icon
 *
 * @props
 *   value        — the text written to the clipboard (required)
 *   label        — the accessible name; also the tooltip default
 *   title        — tooltip text when it should differ from `label`
 *   text         — a visible button label; given it, the block renders a
 *                  `Button` instead of an `IconButton`
 *   statusVisible — render the live region as visible text instead of the
 *                  visually hidden default. Only for a call site whose layout
 *                  has room for a line under the button; an icon inside a row
 *                  must keep the default or the row gains a fourth flex item
 *   reserveStatusLine — keep the visible region in flow AND one line tall even
 *                  while it is empty: the idle region carries a non-breaking
 *                  space, so a call site that reserves the line (a grid cell)
 *                  does not jump when the confirmation appears. It reserves
 *                  EXACTLY ONE line: a message that wraps in the cell still
 *                  grows it, so the caller keeps both sentences one line
 *                  long (InvoiceDetail: 150 px text in a 158-169 px cell).
 *                  It does NOT
 *                  reproduce the model `KopierKnopf`, which rendered an empty
 *                  block and jumped too. Only meaningful together with
 *                  `statusVisible` — without it the region stays visually
 *                  hidden and the prop is a no-op (named in development, see the
 *                  body); a `Stack` caller must NOT set it, or the idle state
 *                  gains a spacing step
 *   failText     — the failure sentence; defaults to `COPY_FAIL`. A call site
 *                  whose value stands VISIBLY next to the button may name the
 *                  next step instead (see `InvoiceDetail.jsx`) — a shared
 *                  sentence cannot carry a step that is only true at one site.
 *                  It must NOT equal `COPY_OK`: the success state is held in
 *                  its own state, not read back out of this text
 *   iconSx       — style for the two icons (the size the call site needs)
 *   onCopied     — called after a successful write
 *   onFailed     — called when the clipboard is unavailable or the write failed
 *   ...rest      — forwarded to the underlying `Button` / `IconButton`
 */
export default function CopyButton({
  value,
  label = 'Copy',
  title,
  text,
  statusVisible = false,
  reserveStatusLine = false,
  failText = COPY_FAIL,
  iconSx,
  sx,
  onCopied,
  onFailed,
  ...rest
}) {
  const [message, setMessage] = useState('')
  // The success state is held here, NOT read back out of `message`. Deriving
  // it from `message === COPY_OK` tied it to a string a call site can set: a
  // `failText={COPY_OK}` would have turned a failure into a success — right
  // symbol, wrong statement. `failText` is a caller's sentence; it must not be
  // able to flip the state.
  const [geklappt, setGeklappt] = useState(false)
  const timer = useRef(null)

  // A pending reset must not fire into an unmounted component.
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

  const handle = async () => {
    const ok = await copyToClipboard(value)
    setGeklappt(ok)
    setMessage(ok ? COPY_OK : failText)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(() => { setMessage(''); setGeklappt(false) }, RUECKMELDUNG_MS)
    if (ok) onCopied?.()
    else onFailed?.()
  }

  const ok = geklappt
  // `fontSize: 'inherit'` is the default so the symbol takes the size of the
  // button it sits in (the model does the same). A call site that needs an
  // explicit size passes `iconSx` — it then applies to BOTH symbols, so the
  // copy and the check mark stay the same size.
  //
  // The confirmation COLOUR is not the call site's to set: `iconSx` is spread
  // FIRST and `success.main` written over it. An `iconSx` that carried a
  // colour would otherwise turn the check mark back to the idle grey and undo
  // the one visible signal there is on a touch device.
  const symbolSx = { fontSize: 'inherit', ...iconSx }
  const icon = ok
    ? <CheckIcon sx={{ ...symbolSx, color: 'success.main' }} />
    : <ContentCopyIcon sx={symbolSx} />
  // `reserveStatusLine` without `statusVisible` is a silent no-op: the region
  // keeps `visuallyHidden`, and the caller believes the line is reserved. Named
  // here, and said out loud in development — house form `EmptyState.jsx` warns
  // instead of throwing, because a caller that sets one prop of a pair is a
  // defect and not a crash.
  if (process.env.NODE_ENV !== 'production' && reserveStatusLine && !statusVisible) {
    // eslint-disable-next-line no-console
    console.warn(
      'CopyButton: `reserveStatusLine` ohne `statusVisible` ist wirkungslos — ' +
      'die Region bleibt `visuallyHidden`. Setze beide oder keine.'
    )
  }
  // Visible only while it carries text — see the note on `statusVisible` above:
  // an empty block would cost the caller's `Stack` a spacing step, and `mt: 0`
  // cannot take it back. `reserveStatusLine` is the caller that wants the line
  // in flow from the first paint instead. Same node either way, so the region
  // is announced.
  const statusSx = statusVisible && (reserveStatusLine || message)
    ? { display: 'block', fontSize: FS.sm, color: 'text.secondary' }
    : visuallyHidden
  // 🔴 Die Reservierung ist das GESCHUETZTE LEERZEICHEN, nicht `display: block`:
  // eine leere Blockbox hat keine Zeilenbox und ist 0 px hoch — genau der Stand,
  // den die Wiedervorlage 2 an beiden Toren beanstandet hat (WV1-W2). Das
  // Zeichen traegt dieselbe Schriftgroesse wie die spaetere Bestaetigung
  // (`statusSx` setzt `FS.sm` fuer beide), die Zeilenhoehe aendert sich beim
  // Wechsel also nicht. Hausmuster: `components/common/StatTile.jsx:396`.
  const reserviert = statusVisible && reserveStatusLine
  const copied = ok ? 'true' : 'false'

  return (
    <>
      {text ? (
        <Button size="small" onClick={handle} startIcon={icon} aria-label={label} data-copied={copied} sx={sx} {...rest}>
          {ok ? COPY_OK : text}
        </Button>
      ) : (
        <Tooltip title={ok ? COPY_OK : (title || label)} placement="top" describeChild>
          <IconButton size="small" aria-label={label} onClick={handle} data-copied={copied} sx={sx} {...rest}>
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <Box component="span" role="status" aria-live="polite" sx={statusSx}>
        {message || (reserviert ? ' ' : '')}
      </Box>
    </>
  )
}
